import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Download, Lock, FileText, BookOpen, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LibrarySection — Blocco "Libreria" all'interno della pagina prodotto.
 *
 * Mostra 3 tessere:
 *   • Anteprima      — sempre scaricabile (backend endpoint pubblico)
 *   • Prima Edizione — Scarica se acquistato, altrimenti "🔒 Acquista il libro"
 *   • Seconda Edizione — stessa logica
 *
 * Entrambe le edizioni si sbloccano con l'UNICO acquisto del libro (€27).
 * Nessun URL pubblico permanente per i PDF completi: ogni download passa
 * per il backend che verifica cookie di auth + acquisto Stripe confermato.
 */
export default function LibrarySection({ product, onAddToCart }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { items: cartItems } = useCart();
  const navigate = useNavigate();

  const slug = product.slug;
  const [entitlements, setEntitlements] = useState({
    loaded: false,
    owns: false,
    editionsUnlocked: [],
  });
  const [busyEdition, setBusyEdition] = useState(null);
  const [error, setError] = useState("");

  const fetchEntitlements = useCallback(async () => {
    if (!isAuthenticated) {
      setEntitlements({ loaded: true, owns: false, editionsUnlocked: [] });
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/library/entitlements/${slug}`,
        { withCredentials: true }
      );
      setEntitlements({
        loaded: true,
        owns: !!data.owns,
        editionsUnlocked: data.editions_unlocked || [],
      });
    } catch {
      setEntitlements({ loaded: true, owns: false, editionsUnlocked: [] });
    }
  }, [isAuthenticated, slug]);

  useEffect(() => {
    if (isLoading) return;
    fetchEntitlements();
  }, [isLoading, fetchEntitlements]);

  const downloadAnteprima = () => {
    setError("");
    // Endpoint pubblico — apre il PDF in una nuova scheda (browser gestisce
    // download nativo grazie a Content-Disposition inline della FileResponse).
    window.open(
      `${API}/api/library/anteprima/${slug}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const downloadEdition = async (edition) => {
    setError("");
    if (!isAuthenticated) {
      // Ridireziona al login mantenendo il percorso di ritorno.
      navigate(
        `/auth?next=${encodeURIComponent(window.location.pathname)}`
      );
      return;
    }
    setBusyEdition(edition);
    try {
      const res = await axios.get(
        `${API}/api/library/download/${slug}/${edition}`,
        { withCredentials: true, responseType: "blob" }
      );
      // Estrai filename dal Content-Disposition (fallback: slug).
      const cd = res.headers["content-disposition"] || "";
      const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ""))
        : `TIME-${edition}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(
          `/auth?next=${encodeURIComponent(window.location.pathname)}`
        );
        return;
      }
      if (err?.response?.status === 403) {
        setError(
          "Devi acquistare il libro per accedere a questa edizione."
        );
        // Riallinea stato entitlement.
        fetchEntitlements();
        return;
      }
      setError(
        err?.response?.data?.detail ||
          "Impossibile scaricare il file. Riprova tra un momento."
      );
    } finally {
      setBusyEdition(null);
    }
  };

  const handleBuyLocked = () => {
    setError("");
    const alreadyInCart = cartItems.some((it) => it.slug === product.slug);
    if (!alreadyInCart) onAddToCart();
    navigate("/carrello");
  };

  const unlocked = (edition) =>
    entitlements.editionsUnlocked.includes(edition);

  return (
    <section
      data-testid="library-section"
      className="mt-16 sm:mt-20 max-w-4xl mx-auto"
    >
      <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/70">
        Libreria
      </div>
      <h2 className="mt-4 text-2xl sm:text-3xl font-extralight tracking-tight text-white">
        Anteprima, Prima e Seconda Edizione.
      </h2>
      <p className="mt-3 text-white/55 font-light leading-relaxed">
        L'anteprima è libera per tutti. L'acquisto sblocca in modo definitivo
        entrambe le edizioni complete, accessibili dalla tua area anche in
        sessioni future.
      </p>

      <div className="mt-8 grid gap-4 sm:gap-5 md:grid-cols-3">
        {/* Anteprima — sempre libera */}
        <LibraryCard
          testId="lib-anteprima"
          icon={<FileText className="w-4 h-4" />}
          title="Anteprima"
          meta="PDF · Libera"
          state="open"
          actionLabel="Scarica"
          actionTestId="lib-anteprima-download"
          onAction={downloadAnteprima}
          hint="Nessun login richiesto"
        />

        {/* Prima Edizione */}
        <EditionCard
          edition="prima"
          label="Prima Edizione"
          meta="Capitoli I–X · PDF"
          unlocked={unlocked("prima")}
          loading={
            !entitlements.loaded || isLoading || busyEdition === "prima"
          }
          isAuthenticated={isAuthenticated}
          onDownload={() => downloadEdition("prima")}
          onBuy={handleBuyLocked}
        />

        {/* Seconda Edizione */}
        <EditionCard
          edition="seconda"
          label="Seconda Edizione"
          meta="Capitoli XI–XX · PDF"
          unlocked={unlocked("seconda")}
          loading={
            !entitlements.loaded || isLoading || busyEdition === "seconda"
          }
          isAuthenticated={isAuthenticated}
          onDownload={() => downloadEdition("seconda")}
          onBuy={handleBuyLocked}
        />
      </div>

      {error && (
        <p
          data-testid="library-error"
          className="mt-5 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-3 py-2"
        >
          {error}
        </p>
      )}

      {!isAuthenticated && !isLoading && (
        <p
          data-testid="library-login-hint"
          className="mt-6 text-[12px] text-white/45 font-light leading-relaxed"
        >
          Hai già acquistato TIME?{" "}
          <Link
            to={`/auth?next=${encodeURIComponent(window.location.pathname)}`}
            className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
          >
            Accedi
          </Link>{" "}
          per ritrovare le tue edizioni sbloccate.
        </p>
      )}
    </section>
  );
}

function LibraryCard({
  testId,
  icon,
  title,
  meta,
  state,
  actionLabel,
  actionTestId,
  onAction,
  hint,
  disabled,
  loading,
}) {
  const isLocked = state === "locked";
  return (
    <div
      data-testid={testId}
      data-state={state}
      className={`p-5 rounded-2xl border ${
        isLocked
          ? "bg-white/[0.015] border-white/5"
          : "bg-white/[0.03] border-white/10"
      } flex flex-col`}
    >
      <div className="flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-mono text-white/60">
        <span
          className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
            isLocked
              ? "bg-white/5 text-white/40"
              : "bg-emerald-400/10 text-emerald-300 border border-emerald-400/25"
          }`}
        >
          {icon}
        </span>
        <span>{title}</span>
      </div>
      <div className="mt-2 text-[10px] tracking-[0.24em] uppercase text-white/35 font-mono">
        {meta}
      </div>

      <button
        type="button"
        data-testid={actionTestId}
        onClick={onAction}
        disabled={disabled || loading}
        className={`mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[11px] tracking-[0.24em] uppercase font-medium transition-colors disabled:opacity-60 ${
          isLocked
            ? "bg-white/[0.06] text-white/80 hover:bg-white/10 border border-white/10"
            : "bg-emerald-400 text-black hover:bg-emerald-300"
        }`}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : isLocked ? (
          <Lock className="w-3.5 h-3.5" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        <span>{actionLabel}</span>
      </button>

      {hint && (
        <p className="mt-3 text-[10px] tracking-[0.18em] uppercase text-white/30 font-mono">
          {hint}
        </p>
      )}
    </div>
  );
}

function EditionCard({
  edition,
  label,
  meta,
  unlocked,
  loading,
  isAuthenticated,
  onDownload,
  onBuy,
}) {
  if (unlocked) {
    return (
      <LibraryCard
        testId={`lib-${edition}`}
        icon={<BookOpen className="w-4 h-4" />}
        title={label}
        meta={meta}
        state="open"
        actionLabel="Scarica"
        actionTestId={`lib-${edition}-download`}
        onAction={onDownload}
        loading={loading}
        hint="Sbloccato dal tuo acquisto"
      />
    );
  }
  return (
    <LibraryCard
      testId={`lib-${edition}`}
      icon={<Lock className="w-4 h-4" />}
      title={label}
      meta={meta}
      state="locked"
      actionLabel={
        isAuthenticated ? "Acquista il libro" : "Accedi e acquista"
      }
      actionTestId={`lib-${edition}-buy`}
      onAction={onBuy}
      loading={loading}
      hint="Sblocca con l'acquisto del libro (€ 27)"
    />
  );
}
